import { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Alert } from "@mui/material";
import jsyaml from "js-yaml";
import SwaggerUIBundle from "swagger-ui-dist/swagger-ui-bundle.js";
import "swagger-ui-dist/swagger-ui.css";
import "./swagger-dark.css";
import Header from "../../components/Header";
import { startToast, updateToastSuccess } from "../../utils/notification-loading";

// Documentazione della reseller-api (Partner API), stessa pagina Swagger UI dell'API
// pubblica on-prem. Lo spec (public/reseller-openapi.yaml) è generato da quello
// on-prem: non va modificato a mano. Bundle vanilla `swagger-ui-dist` invece di
// `swagger-ui-react`, che va in errore runtime su CRA5.

// "Try it out" deve chiamare la reseller-api della build corrente: stesso host
// dell'API della dashboard (REACT_APP_API_URL, in produzione securitylecs.com),
// porta 5001. REACT_APP_RESELLER_API_URL lo sovrascrive se l'API è altrove.
const resellerApiBase = () => {
  if (process.env.REACT_APP_RESELLER_API_URL) return process.env.REACT_APP_RESELLER_API_URL;
  if (process.env.REACT_APP_API_URL) return `https://${new URL(process.env.REACT_APP_API_URL).hostname}:5001`;
  if (process.env.NODE_ENV === "production") return "https://securitylecs.com:5001";
  return `https://${window.location.hostname}:5001`;
};

// Swagger UI mostra un array con valori ammessi (es. `sections` di /v2/report) come
// lista a scorrimento. Lo sostituiamo con una tendina a spunte: nessuna voce selezionata
// di default (il parametro non viene inviato), una o più voci a scelta.
// swagger-ui-bundle ha una copia di React propria: componente e hook devono usare quella
// (system.React), non quella dell'app, altrimenti "Invalid hook call" e i parametri spariscono.
const EnumMultiSelectPlugin = () => ({
  wrapComponents: {
    JsonSchema_array: (Original, system) => {
      const R = system.React;
      const h = R.createElement;

      const MultiSelect = ({ value, onChange, allowed, disabled }) => {
        const [open, setOpen] = R.useState(false);
        const wrapRef = R.useRef(null);
        const selected = Array.from(value && value.toJS ? value.toJS() : Array.isArray(value) ? value : []);

        R.useEffect(() => {
          const close = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
          document.addEventListener("mousedown", close);
          return () => document.removeEventListener("mousedown", close);
        }, []);

        const toggle = (item) => {
          const next = selected.includes(item) ? selected.filter((v) => v !== item) : [...selected, item];
          onChange(allowed.filter((v) => next.includes(v)));
        };

        // Riga = div (non label): Swagger UI stila le label e centra/allarga le checkbox.
        const renderItem = (item) =>
          h("div", { key: item, className: "enum-multiselect__item", onClick: () => toggle(item) },
            h("input", { type: "checkbox", checked: selected.includes(item), readOnly: true, tabIndex: -1 }),
            h("span", null, item));

        return h("div", { ref: wrapRef, className: "enum-multiselect" },
          h("button", { type: "button", className: "enum-multiselect__toggle", disabled, onClick: () => setOpen(!open) },
            h("span", null, selected.length ? `${selected.length} selected: ${selected.join(", ")}` : "Select sections (none = all)"),
            h("span", null, open ? "▲" : "▼")),
          open && h("div", { className: "enum-multiselect__menu" },
            selected.length > 0 && h("button", { type: "button", className: "enum-multiselect__clear", onClick: () => onChange([]) }, "Clear selection"),
            allowed.map(renderItem)));
      };

      return (props) => {
        const allowed = props.schema && props.schema.getIn ? props.schema.getIn(["items", "enum"]) : null;
        const list = allowed && allowed.toJS ? allowed.toJS() : allowed;
        if (!Array.isArray(list) || !list.length) return h(Original, props);
        return h(MultiSelect, { value: props.value, onChange: props.onChange, allowed: list, disabled: props.disabled });
      };
    },
  },
});

const ApiDocs = () => {
  const containerRef = useRef(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const container = containerRef.current;

    // I pulsanti "copy to clipboard" di Swagger UI non danno feedback: li
    // intercettiamo via delegation e mostriamo il toast standard dell'app.
    const handleCopyClick = (e) => {
      if (e.target.closest(".copy-to-clipboard")) {
        startToast();
        updateToastSuccess("Copied to clipboard");
      }
    };
    container?.addEventListener("click", handleCopyClick);

    fetch("/reseller-openapi.yaml")
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((text) => {
        if (!alive) return;
        const spec = jsyaml.load(text);
        spec.servers = [{ url: resellerApiBase(), description: "LECS Partner API" }];
        SwaggerUIBundle({
          spec,
          domNode: container,
          deepLinking: false,
          docExpansion: "list",
          defaultModelsExpandDepth: 1,
          tryItOutEnabled: true,
          presets: [SwaggerUIBundle.presets.apis],
          plugins: [EnumMultiSelectPlugin],
          layout: "BaseLayout",
        });
        setLoading(false);
      })
      .catch(() => {
        if (alive) { setError("Unable to load the API specification."); setLoading(false); }
      });

    return () => {
      alive = false;
      container?.removeEventListener("click", handleCopyClick);
    };
  }, []);

  return (
    <Box m="20px">
      <Box mb={2}>
        <Header title="API Documentation" subtitle="LECS Partner API reference" />
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && !error && (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      )}
      {/* Tema scuro applicato via .swagger-dark (vedi swagger-dark.css). */}
      <Box ref={containerRef} className="swagger-dark" />
    </Box>
  );
};

export default ApiDocs;
